// src/components/ui/ControlHover.tsx
import * as React from "react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { ControlDetail, CONTROL_DETAILS } from "@/lib/hoverDetails";

function generateControlDetails(code: string): ControlDetail {
  return {
    title: `Control ${code}`,
    summary: `Control ${code} helps reduce cybersecurity risk by enforcing structured security practices.`,
    why: [
      "Establishes clear security expectations.",
      "Improves consistency of implementation.",
      "Supports compliance and audit readiness.",
    ],
    goodLooksLike: [
      "Documented and approved control.",
      "Consistent implementation.",
      "Measured effectiveness.",
    ],
    evidence: [
      "Policy or standard document.",
      "Implementation configuration screenshot.",
      "Recent audit or review record.",
    ],
  };
}

function getLookupCandidates(code: string): string[] {
  const raw = code.trim().toUpperCase();
  const candidates = new Set<string>([raw]);

  const withoutNistPrefix = raw.replace(/^NIST-CSF-/, "").replace(/^NIST-/, "");
  candidates.add(withoutNistPrefix);

  const dotted = withoutNistPrefix
    .replace(/^([A-Z]{2})-([A-Z]{2})-(\d{2,3})$/, "$1.$2-$3")
    .replace(/^([A-Z]{2})-([A-Z]{2})$/, "$1.$2");
  candidates.add(dotted);

  const baseWithoutIndex = dotted.replace(/-\d{1,3}$/, "");
  candidates.add(baseWithoutIndex);

  return [...candidates].filter(Boolean);
}

function resolveControlDetail(code: string): {
  detail: ControlDetail;
  matchedKey: string | null;
} {
  const candidates = getLookupCandidates(code);
  for (const candidate of candidates) {
    const detail = CONTROL_DETAILS[candidate];
    if (detail) {
      return { detail, matchedKey: candidate };
    }
  }

  return {
    detail: generateControlDetails(code),
    matchedKey: null,
  };
}

function Content({ detail }: { detail: ControlDetail }) {
  return (
    <div className="max-w-sm space-y-3">
      <div className="text-sm font-semibold">{detail.title}</div>
      <p className="text-xs text-muted-foreground">{detail.summary}</p>

      <div className="text-xs">
        <h4 className="font-bold mt-2 mb-1">Why it matters</h4>
        <ul className="list-disc pl-4 space-y-0.5">
          {detail.why.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      </div>

      <div className="text-xs">
        <h4 className="font-bold mt-2 mb-1">What &ldquo;good&rdquo; looks like</h4>
        <ul className="list-disc pl-4 space-y-0.5">
          {detail.goodLooksLike.map((g, i) => (
            <li key={i}>{g}</li>
          ))}
        </ul>
      </div>

      <div className="text-xs">
        <h4 className="font-bold mt-2 mb-1">Example evidence</h4>
        <ul className="list-disc pl-4 space-y-0.5">
          {detail.evidence.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function ControlHover({
  code,
  children,
  className,
}: {
  code: string;
  children: React.ReactNode;
  className?: string;
}) {
  const directLookup = CONTROL_DETAILS[code] ?? null;
  const { detail, matchedKey } = resolveControlDetail(code);

  if (import.meta.env.DEV) {
    console.log("CONTROL LOOKUP:", code, directLookup);
    if (!directLookup) {
      console.log("CONTROL LOOKUP RESOLVED:", code, matchedKey ?? "generated-fallback");
    }
  }

  const base =
    "inline-flex items-center rounded-full px-2 py-1 text-xs md:text-sm border select-none " +
    (className ?? "");

  return (
    <HoverCard openDelay={150}>
      <HoverCardTrigger asChild>
        <span
          className={base + " cursor-help"}
          tabIndex={0}
          role="button"
          aria-label={`${code} info`}
        >
          {children}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="start"
        sideOffset={8}
        className="z-[9999] text-foreground"
      >
        <Content detail={detail} />
      </HoverCardContent>
    </HoverCard>
  );
}